"use client";

import { useMemo, useSyncExternalStore } from "react";
import { stegaClean } from "@sanity/client/stega";
import {
  type IBannerCountdown,
  type ITimeLeft,
  getCountdownSecondsLeft,
  splitSeconds,
} from "@/lib/bannerCountdown";

function subscribeToSeconds(onChange: () => void) {
  const id = window.setInterval(onChange, 1000);
  return () => window.clearInterval(id);
}

/**
 * Time left on a banner's countdown, ticking every second; null while it shouldn't show
 * (before its start, after its end). The banner itself stays either way.
 *
 * Hydration uses `renderedAt` (the server's clock when it rendered the page) so it matches the
 * server markup, then the visitor's clock takes over.
 */
export function useBannerCountdown(
  { showCountdown, countdownStartDate, countdownEndDate }: IBannerCountdown,
  renderedAt: number,
): ITimeLeft | null {
  // draft mode adds invisible stega characters to strings, which break date parsing
  const countdown = useMemo(
    () => stegaClean({ showCountdown, countdownStartDate, countdownEndDate }),
    [showCountdown, countdownStartDate, countdownEndDate],
  );

  const secondsLeft = useSyncExternalStore(
    subscribeToSeconds,
    () => getCountdownSecondsLeft(countdown, Date.now()),
    () => getCountdownSecondsLeft(countdown, renderedAt),
  );

  return secondsLeft === null ? null : splitSeconds(secondsLeft);
}
